import React, { useMemo } from "react";

import toShort from "../../utils/dateParser";
import { toDateInputValue, todayIsoDate } from "../../utils/orderDates";
import styles from "./OrderDetail.module.css";

const MILESTONE_KEYS = ["dateIn", "workStarted", "workCompleted", "dateOut"];

const STAGE_LABELS = ["Date in", "Work started", "Work completed", "Date out"];

function getTodayPlacement(order) {
  const dates = MILESTONE_KEYS.map((k) => {
    const s = toDateInputValue(order[k]);
    return s || null;
  });
  const today = todayIsoDate();
  if (!dates.some(Boolean)) {
    return { kind: "none" };
  }

  for (let k = 0; k < 4; k++) {
    if (dates[k] === today) {
      return { kind: "onNode", index: k };
    }
  }

  for (let s = 0; s < 3; s++) {
    let left = null;
    for (let i = s; i >= 0; i--) {
      if (dates[i]) {
        left = dates[i];
        break;
      }
    }
    let right = null;
    for (let i = s + 1; i <= 3; i++) {
      if (dates[i]) {
        right = dates[i];
        break;
      }
    }
    if (!left && right && today < right) {
      return { kind: "segment", index: s };
    }
    if (left && right && today > left && today < right) {
      return { kind: "segment", index: s };
    }
    if (left && !right && today > left) {
      return { kind: "segment", index: s };
    }
  }

  let maxD = null;
  for (const d of dates) {
    if (d && (!maxD || d > maxD)) {
      maxD = d;
    }
  }
  let minD = null;
  for (const d of dates) {
    if (d && (!minD || d < minD)) {
      minD = d;
    }
  }
  if (maxD && today > maxD) {
    return { kind: "segment", index: 2 };
  }
  if (minD && today < minD) {
    return { kind: "segment", index: 0 };
  }
  return { kind: "none" };
}

function dashVariant(leftComplete, rightComplete) {
  if (leftComplete && rightComplete) {
    return "green";
  }
  if (!leftComplete && !rightComplete) {
    return "gray";
  }
  return "transition";
}

function OrderTimeline({ order }) {
  const placement = useMemo(() => getTodayPlacement(order), [order]);
  const todayDisplay = toShort(todayIsoDate());

  const milestones = useMemo(() => {
    const today = todayIsoDate();
    return MILESTONE_KEYS.map((key, index) => {
      const iso = toDateInputValue(order[key]) || null;
      const complete = Boolean(iso && iso <= today);
      return {
        key,
        index,
        iso,
        complete,
        label: STAGE_LABELS[index],
      };
    });
  }, [order]);

  return (
    <div className={styles.timeline}>
      <div className={styles.timelineInner}>
        {/* Metadata above track (short date or TBD) */}
        <div className={styles.timelineMetaRow}>
          {milestones.map((m, k) => (
            <React.Fragment key={`m-${m.key}`}>
              {k > 0 ? (
                <div className={styles.timelineGutter}>
                  {placement.kind === "segment" && placement.index === k - 1 ? (
                    <div className={styles.timelineMetaPrimary}>{todayDisplay}</div>
                  ) : null}
                </div>
              ) : null}
              <div className={styles.timelineMetaCell}>
                <div className={styles.timelineMetaPrimary}>
                  {m.iso ? toShort(order[m.key]) : "TBD"}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Circles + connectors */}
        <div className={styles.timelineTrackRow}>
          {milestones.map((m, k) => {
            const seg =
              k > 0 ? dashVariant(milestones[k - 1].complete, m.complete) : null;
            const dashTone =
              seg === "green"
                ? styles.timelineDashGreen
                : seg === "gray"
                  ? styles.timelineDashGray
                  : styles.timelineDashTransition;
            return (
              <React.Fragment key={`t-${m.key}`}>
                {k > 0 ? (
                  <div className={`${styles.timelineDash} ${dashTone}`}>
                    {placement.kind === "segment" && placement.index === k - 1 ? (
                      <span className={styles.timelineDiamond} title="Today">
                        ◆
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <div className={styles.timelineNodeTop}>
                  <div className={styles.timelineNodeMark}>
                    {placement.kind === "onNode" && placement.index === k ? (
                      <span className={styles.timelineDiamondOnNode} title="Today">
                        ◆
                      </span>
                    ) : null}
                    <span
                      className={`${styles.timelineCircle} ${
                        m.complete ? styles.timelineCircleComplete : styles.timelineCircleFuture
                      }`}
                      aria-hidden
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Stage names below (green when date recorded and on/before today) */}
        <div className={styles.timelineStageRow}>
          {milestones.map((m, k) => (
            <React.Fragment key={`s-${m.key}`}>
              {k > 0 ? (
                <div className={styles.timelineGutter}>
                  {placement.kind === "segment" && placement.index === k - 1 ? (
                    <div className={styles.timelineStageTodayLabel}>Today</div>
                  ) : null}
                </div>
              ) : null}
              <div className={styles.timelineStageCell}>
                <div
                  className={`${styles.timelineStageLabel} ${
                    m.complete ? styles.timelineStageLabelComplete : ""
                  }`}
                >
                  {m.label}
                </div>
                {placement.kind === "onNode" && placement.index === k ? (
                  <div className={styles.timelineStageTodayLabel}>Today</div>
                ) : null}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OrderTimeline;
